import moment from 'moment'

/** Canonical buckets for invoice status aliases (API may return either form). */
export const INVOICE_STATUS_ALIAS_MAP = {
  'part paid': 'partial',
  'partially paid': 'partial',
  paid: 'paid',
  unpaid: 'unpaid',
  overdue: 'overdue',
  draft: 'draft',
  raised: 'raised',
  issued: 'issued',
  disputed: 'disputed',
  cancelled: 'cancelled',
  'pending approval': 'pending approval',
  approved: 'approved',
  revised: 'revised',
  'early attrition': 'early attrition',
  returned: 'returned'
}

export default {
  methods: {
    normalizeInvoiceStatusKey(status) {
      const raw = String(status || '').trim().toLowerCase().replace(/\s+/g, ' ')
      if (!raw) return ''
      return INVOICE_STATUS_ALIAS_MAP[raw] || raw
    },

    invoiceStatusesMatch(a, b) {
      const ka = this.normalizeInvoiceStatusKey(a)
      const kb = this.normalizeInvoiceStatusKey(b)
      return !!ka && !!kb && ka === kb
    },

    isPartialPaidStatus(status) {
      return this.normalizeInvoiceStatusKey(status) === 'partial'
    },

    isPaidStatus(status) {
      return this.normalizeInvoiceStatusKey(status) === 'paid'
    },

    isUnpaidStatus(status) {
      return this.normalizeInvoiceStatusKey(status) === 'unpaid'
    },

    formatIndianNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return '0'
      return parseFloat(num).toLocaleString('en-IN', { maximumFractionDigits: 2 })
    },

    formatAmountCompact(num) {
      if (num === null || num === undefined || isNaN(num)) return '₹0'
      const n = parseFloat(num)
      if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`
      if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`
      if (n >= 1000) return `₹${(n / 1000).toFixed(2)} K`
      return `₹${n.toFixed(2)}`
    },

    formatCountCompact(num) {
      if (num === null || num === undefined || isNaN(num)) return '0'
      const n = parseFloat(num)
      if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`
      if (n >= 1000) return `${(n / 1000).toFixed(2)}K`
      return String(Math.round(n))
    },

    formatDate(date) {
      if (!date) return 'N/A'
      try { return moment(date).format('DD-MMM-YYYY') } catch { return 'Invalid Date' }
    },

    formatDateTime(date) {
      if (!date) return 'N/A'
      try { return moment(date).format('DD-MMM-YYYY HH:mm') } catch { return 'Invalid Date' }
    },

    isOverdue(dueDate) {
      if (!dueDate) return false
      try { return moment(dueDate).isBefore(moment(), 'day') } catch { return false }
    },

    getStatusVariant(status) {
      const v = {
        Draft: 'secondary', 'Pending Approval': 'warning', Approved: 'info',
        Issued: 'primary', Raised: 'primary', 'Part Paid': 'warning',
        'Partially Paid': 'warning', Paid: 'success', Unpaid: 'warning',
        Overdue: 'danger', Disputed: 'danger', Cancelled: 'dark',
        Revised: 'info', 'Early Attrition': 'secondary', Returned: 'warning'
      }
      return v[status] || 'secondary'
    },

    getGstr1StatusVariant(status) {
      const v = { 'Not Ready': 'secondary', Ready: 'info', Included: 'primary', Uploaded: 'warning', Filed: 'success' }
      return v[status] || 'secondary'
    },

    getOverdueVariant(days) {
      if (days <= 0) return 'success'
      if (days <= 7) return 'warning'
      if (days <= 15) return 'danger'
      return 'dark'
    },

    getFollowUpVariant(status) {
      const v = { 'Friendly Reminder': 'info', 'Payment Reminder': 'warning', Escalation: 'danger', 'Finance Escalation': 'dark' }
      return v[status] || 'secondary'
    },

    getBillingModelShortLabel(model) {
      if (!model) return 'N/A'
      const m = String(model).toLowerCase()
      if (this.isFteBillingModel(m)) return 'FTE'
      if (m.includes('contractual') || m.includes('staffing')) return 'Contractual'
      if (m.includes('cost')) return 'Cost+'
      if (m.includes('payroll')) return 'Payroll'
      return model
    },

    /** FTE / Permanent / Full Time / Fixed Term billing-model aliases (case-insensitive). */
    isFteBillingModel(model) {
      const m = String(model || '').toLowerCase()
      if (!m) return false
      return (
        m.includes('permanent') ||
        m.includes('fte') ||
        m.includes('recruitment') ||
        m.includes('full time') ||
        m.includes('full-time') ||
        m.includes('fulltime') ||
        m.includes('fixed term') ||
        m.includes('fixed-term') ||
        m.includes('fixedterm')
      )
    },

    /** Contractual / staffing / cost+ billing-model aliases (case-insensitive). */
    isContractualBillingModel(model) {
      const m = String(model || '').toLowerCase()
      if (!m) return false
      return (
        m.includes('contractual') ||
        m.includes('staffing') ||
        m.includes('cost plus') ||
        m.includes('cost+')
      )
    },

    /**
     * Candidate Revenue tab: whether Actions should show Update Revenue.
     * - Contractual / Cost+ White Collar (any status; empty category treated as white collar)
     * - FTE / Full Time / Fixed Term / Permanent when status is Joined
     * Mirrors My Candidates Update Data (FTE) / Update Contractual Revenue (WC contractual).
     * Revenue ₹0 or invoice N/A must NOT hide the action.
     */
    canUpdateCandidateRevenue(item) {
      if (!item) return false
      const model = String(item.billing_model || item.position_type || '')
      const category = String(item.category || '').toLowerCase().trim()
      // my-candidates API uses candidateStatus; mapped rows use candidate_status
      const status = String(
        item.candidate_status || item.candidateStatus || item.status || ''
      ).toLowerCase().trim()

      const isWhiteCollar =
        !category ||
        category.includes('white collar') ||
        category.includes('white-collar') ||
        category.includes('whitecollar')

      if (this.isContractualBillingModel(model) && isWhiteCollar) return true

      const isJoined = status === 'joined'
      if (this.isFteBillingModel(model) && isJoined) return true

      return false
    }
  }
}
